import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import { useQuery } from '@tanstack/react-query';
import { fetchStudies } from 'api/study';
import LabelledDropdown from 'components/atoms/Dropdown/LabelledDropdown';
import LabelledInput from 'components/atoms/LabelledInput/LabelledInput';
import { ALL_STUDIES_VALUE } from 'components/SearchFilters/hooks/useMainFilters';
import AutocompleteFilter from 'components/SearchFilters/MainFilters/AutocompleteFilter';
import { JobType } from 'interfaces/job';
import {
  compact,
  concat,
  filter,
  includes,
  isBoolean,
  isEmpty,
  isNumber,
  map,
  mapValues,
  pick,
  uniqBy,
  values,
} from 'lodash';
import React from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { canQueryUsersEndpoint } from 'utils/canQueryUsersEndpoint';
import {
  CALCULATE_FEATURES_NON_SECONDARY_JOB_TYPE_FILTER,
  mapApiJobsFiltersToUiFilters,
  mapUiJobsFiltersToApiFilters,
  SECONDARY_ANALYSIS_JOB_TYPE_FILTER,
} from 'utils/jobTypeFilters';
import { useJobsQuery } from 'utils/queryHooks/jobs/useJobs';
import { useUiSettings } from 'utils/queryHooks/uiConstantsHooks';
import useUsersById from 'utils/queryHooks/useUsersById';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { usePermissions } from 'utils/usePermissions';
import { getJobTypeDisplayLabel } from './jobTypeLabels';

export enum FilterFields {
  study = 'study',
  jobIds = 'jobIds',
  jobName = 'jobName',
  jobType = 'jobType',
  userName = 'userName',
  source = 'source',
  status = 'status',
  pipelines = 'pipelines',
}

interface FiltersProps {
  filterFields?: FilterFields[];
  sx?: SxProps;
  onChange?: (filters: Record<string, any>) => void;
  isRefreshingFilters?: boolean;
  onFiltersSubmitted?: () => void;
}

export const jobsFiltersDebounceMs = 400;

export const getJobTypeFilterOptions = () => {
  const baseOptions = filter(
    map(JobType, (jobType) => {
      if (jobType === JobType.PostProcessingAndCalculateFeatures) {
        return {
          value: CALCULATE_FEATURES_NON_SECONDARY_JOB_TYPE_FILTER,
          text: 'Calculate Features',
        };
      }

      return {
        value: jobType,
        text: getJobTypeDisplayLabel(jobType),
      };
    }),
    ({ value }) => value !== JobType.VentanaMock
  );

  return [
    ...baseOptions,
    {
      value: SECONDARY_ANALYSIS_JOB_TYPE_FILTER,
      text: 'Secondary Analysis',
    },
  ];
};

const Filters: React.FunctionComponent<React.PropsWithChildren<FiltersProps>> = ({
  filterFields = values(FilterFields),
  sx,
  onChange,
  isRefreshingFilters = false,
  onFiltersSubmitted,
}) => {
  const jobsFilterLabel = (label: string) => (onChange ? label : `Jobs: ${label}`);
  const { labId } = useCurrentLabId();
  const { hasPermission } = usePermissions();
  const canFilterByUser = canQueryUsersEndpoint(hasPermission);
  const { uiSettings } = useUiSettings();
  const enumDisplayNames = uiSettings.enumDisplayNames;

  const { data: studies, isLoading: studiesLoading } = useQuery(['studies', labId], () => fetchStudies(labId));
  let studyOptions: any = null;

  if (!isEmpty(studies)) {
    studyOptions = map(studies, (study) => ({
      value: study.id,
      text: study.name,
    }));

    studyOptions = concat({ value: ALL_STUDIES_VALUE, text: 'All' }, studyOptions);
  }

  const senderOptions = map(enumDisplayNames.requestSender, (requestSender) => {
    return {
      value: requestSender.value,
      text: requestSender?.label,
    };
  });

  const statusOptions = map(enumDisplayNames.jobStatus, (jobStatus) => {
    return {
      value: jobStatus.value,
      text: jobStatus?.label,
    };
  });

  const typesOptions = getJobTypeFilterOptions();

  const { usersById, getUserNameById, isLoadingUsers } = useUsersById({ enabled: canFilterByUser });
  const usersOptions = map(uniqBy(values(usersById), 'id'), (user) => ({
    value: user.id,
    text: getUserNameById(user.id) || user.id,
  }));
  const getUserSearchText = (userId: string) => {
    const user = usersById[userId];
    const userName = getUserNameById(userId);
    const emailPrefix = user?.primaryEmail?.split('@')[0];

    return [userName, user?.primaryEmail, emailPrefix, user?.auth0UserId, user?.id].filter(Boolean).join(' ');
  };

  const {
    queryParams: { jobsFilters, filters: proceduresFilters },
    setQueryParams,
  } = useEncodedFilters();
  const shouldLoadJobIdOptions = includes(filterFields, FilterFields.jobIds);
  const jobIdOptionsQuery = useJobsQuery(
    {
      filters: {
        studyId: proceduresFilters?.studyId === ALL_STUDIES_VALUE ? undefined : proceduresFilters?.studyId,
      } as any,
      labId,
      pageSize: 100,
    },
    {
      enabled: shouldLoadJobIdOptions,
      queriedEntityName: 'job',
    }
  );
  const jobIdOptions = map(compact(map(jobIdOptionsQuery.data?.jobs, 'id')), (jobId) => ({
    value: jobId,
    text: jobId,
  }));

  // we are using a local state as a middleman to debounce the query params update which triggers a refetch of the data
  const [filters, setFilters] = React.useState<Record<string, any>>(() => mapApiJobsFiltersToUiFilters(jobsFilters));
  const [isApplyingFilters, setIsApplyingFilters] = React.useState(false);

  const updateJobFiltersQueryParams = useDebouncedCallback((updatedFilters: Record<string, any>) => {
    setQueryParams({ jobsFilters: mapUiJobsFiltersToApiFilters(updatedFilters) }, 'replaceIn');
    setIsApplyingFilters(false);
    onFiltersSubmitted?.();
  }, jobsFiltersDebounceMs);

  const handleFilterChange = (filterKey: string, value: any) => {
    const newFilters = { ...filters, [filterKey]: value };
    setFilters(newFilters);
    const normalizedFilters = mapValues(newFilters, (filterValue) =>
      !isNumber(filterValue) && !isBoolean(filterValue) && isEmpty(filterValue) ? undefined : filterValue
    );
    if (onChange) {
      onChange(mapUiJobsFiltersToApiFilters(normalizedFilters));
    } else {
      setIsApplyingFilters(true);
      updateJobFiltersQueryParams(normalizedFilters);
    }
  };

  const filterStatusText = isApplyingFilters ? 'Applying filters...' : isRefreshingFilters ? 'Filtering jobs...' : null;

  const showStatusQuickFilters = includes(filterFields, FilterFields.status) && !isEmpty(statusOptions);
  const selectedStatuses: string[] = filters?.statuses || [];

  const toggleStatusQuickFilter = (statusValue: string) => {
    const nextStatuses = includes(selectedStatuses, statusValue)
      ? filter(selectedStatuses, (value) => value !== statusValue)
      : [...selectedStatuses, statusValue];
    handleFilterChange('statuses', nextStatuses);
  };

  const filterComponents = {
    [FilterFields.study]: (
      <LabelledDropdown
        size="small"
        label="Study"
        options={studyOptions}
        value={proceduresFilters?.studyId}
        onOptionSelected={(optionValue) => {
          setQueryParams({ filters: { studyId: optionValue } });
        }}
        loading={studiesLoading}
      />
    ),
    [FilterFields.jobIds]: (
      <AutocompleteFilter
        label={jobsFilterLabel('Job IDs')}
        options={jobIdOptions}
        limitTags={1}
        handleChange={(optionValue) => handleFilterChange('jobIds', optionValue)}
        value={filters?.jobIds || []}
        loading={jobIdOptionsQuery.isLoading}
        allowCustomValues
      />
    ),
    [FilterFields.jobName]: (
      <LabelledInput
        size="small"
        label={jobsFilterLabel('Job Name')}
        value={filters?.jobName || []}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('jobName', event.target.value)}
      />
    ),
    [FilterFields.jobType]: (
      <AutocompleteFilter
        label={jobsFilterLabel('Job Type')}
        options={typesOptions}
        limitTags={1}
        handleChange={(optionValue) => {
          handleFilterChange('types', optionValue);
        }}
        value={filters?.types || []}
      />
    ),
    [FilterFields.userName]: canFilterByUser ? (
      <AutocompleteFilter
        label={jobsFilterLabel('Submitted By / ID')}
        options={usersOptions}
        limitTags={1}
        handleChange={(optionValue) => {
          handleFilterChange('users', optionValue);
        }}
        value={filters?.users || []}
        loading={isLoadingUsers}
        allowCustomValues
        getSearchText={(option) => getUserSearchText(option.value)}
        inputType="search"
        inputAttributes={{
          autoComplete: 'off',
          name: 'jobs-user-filter',
          'data-lpignore': 'true',
          'data-form-type': 'other',
        }}
      />
    ) : null,
    [FilterFields.source]: (
      <AutocompleteFilter
        label={jobsFilterLabel('Source')}
        options={senderOptions}
        limitTags={1}
        handleChange={(optionValue) => {
          handleFilterChange('senders', optionValue);
        }}
        value={filters?.senders || []}
      />
    ),
    [FilterFields.status]: (
      <AutocompleteFilter
        label={jobsFilterLabel('Status')}
        options={statusOptions}
        limitTags={1}
        handleChange={(optionValue) => {
          handleFilterChange('statuses', optionValue);
        }}
        value={filters?.statuses || []}
      />
    ),
    [FilterFields.pipelines]: (
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(filters?.includePipelines)}
            onChange={(event) => handleFilterChange('includePipelines', event.target.checked)}
          />
        }
        label={jobsFilterLabel('Include pipelines')}
      />
    ),
  };

  return (
    <Grid my={2} container spacing={1} wrap="wrap" sx={sx}>
      {map(pick(filterComponents, filterFields), (component, key) =>
        component ? (
          <Grid item flex={1} minWidth="200px" maxWidth="300px" key={key}>
            {component}
          </Grid>
        ) : null
      )}
      {showStatusQuickFilters ? (
        <Grid item xs={12} container spacing={1} alignItems="center">
          <Grid item>
            <Typography variant="body2" color="text.secondary">
              {onChange ? 'Quick status filters:' : 'Jobs status:'}
            </Typography>
          </Grid>
          <Grid item xs>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {map(statusOptions, (statusOption) => {
                const isSelected = includes(selectedStatuses, statusOption.value);
                return (
                  <Chip
                    key={statusOption.value}
                    label={statusOption.text}
                    size="small"
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    onClick={() => toggleStatusQuickFilter(statusOption.value)}
                  />
                );
              })}
            </Stack>
          </Grid>
        </Grid>
      ) : null}
      {filterStatusText ? (
        <Grid item xs={12}>
          <Alert severity="info">{filterStatusText}</Alert>
        </Grid>
      ) : null}
    </Grid>
  );
};

export default Filters;
